import {
  GeneralLedgerChartAccountListAccodianType,
  GeneralLedgerJournalAccodianType,
  JournalType,
  RawAccountData,
} from "../types";

// Function to format the data into the requested structure
export function formatAccountData(
  data: RawAccountData[]
): GeneralLedgerChartAccountListAccodianType[] {
  // Filter out records with empty name first
  const validData = data.filter((item) => item.name.trim() !== "");

  // First, group by name (main type)
  const groupedByName: Record<
    string,
    GeneralLedgerChartAccountListAccodianType
  > = {};

  validData.forEach((item) => {
    const {
      name,
      accountSubTypeName,
      accountName,
      amount,
      categoryId,
      accountSubTypeId,
      accountId,
    } = item;

    // Create main group if it doesn't exist
    if (!groupedByName[name]) {
      groupedByName[name] = {
        name: name,
        type: name,
        sub_type: name,
        balance: "0",
        children: [],
        categoryId,
        accountSubTypeId,
        accountId,
        level: "parent", // Always set top-level items as parent
      };
    }

    // Add to main group balance
    const currentBalance = parseFloat(groupedByName[name].balance || "0");
    groupedByName[name].balance = (currentBalance + amount).toString();

    // Find or create subtype group
    let subTypeGroup = groupedByName[name].children?.find(
      (child) => child.name === accountSubTypeName
    );

    if (!subTypeGroup) {
      subTypeGroup = {
        name: accountSubTypeName,
        balance: "0",
        type: name,
        children: [],
        sub_type: accountSubTypeName,
        categoryId,
        accountSubTypeId,
        accountId,
        level: "child", // Set middle-level items as child
      };
      groupedByName[name].children?.push(subTypeGroup);
    }

    // Add to subtype balance
    if (subTypeGroup) {
      const subBalance = parseFloat(subTypeGroup.balance || "0");
      subTypeGroup.balance = (subBalance + amount).toString();

      // Add account as child of subtype
      if (!subTypeGroup.children) {
        subTypeGroup.children = [];
      }

      subTypeGroup.children.push({
        name: accountName,
        type: name,
        sub_type: accountSubTypeName,
        balance: amount.toString(),
        categoryId,
        accountSubTypeId,
        accountId,
        level: "grandchild", // Set leaf items as grandchild
      });
    }
  });

  // Convert the object to array and ensure all top-level items are marked as parent
  return Object.values(groupedByName).map((group) => {
    // Ensure top-level items are always parent
    group.level = "parent";

    // Format balance to 2 decimal places
    if (group.balance) {
      const numBalance = parseFloat(group.balance);
      group.balance = numBalance.toFixed(2);
    }

    // Sort children by name
    if (group.children) {
      group.children.sort((a, b) => a.name.localeCompare(b.name));

      // Format sub-balances and ensure child level
      group.children.forEach((subGroup) => {
        subGroup.level = "child"; // Ensure middle-level items are child
        if (subGroup.balance) {
          const numSubBalance = parseFloat(subGroup.balance);
          subGroup.balance = numSubBalance.toFixed(2);
        }

        // Sort account children
        if (subGroup.children) {
          subGroup.children.sort((a, b) => a.name.localeCompare(b.name));

          // Format account balances and ensure grandchild level
          subGroup.children.forEach((account) => {
            account.level = "grandchild"; // Ensure leaf items are grandchild
            if (account.balance) {
              const numAccountBalance = parseFloat(account.balance);
              account.balance = numAccountBalance.toFixed(2);
            }
          });
        }
      });
    }

    return group;
  });
}

export const formateJournalData = (
  data: JournalType[]
): GeneralLedgerJournalAccodianType[] => {
  // Group by voucherId
  const groupedByVoucher: Record<number, any> = {};

  data.forEach((entry) => {
    const {
      voucherId,
      voucherDate,
      name,
      transactionType,
      accountName,
      debitAmt,
      creditAmt,
    } = entry;

    // Create voucher group if it doesn't exist
    if (!groupedByVoucher[voucherId]) {
      groupedByVoucher[voucherId] = {
        id: voucherId.toString(),
        voucherId,
        voucherDate,
        name,
        transactionType,
        accountName: "",
        debitAmt: 0,
        creditAmt: 0,
        children: [],
      };
    }

    // Add entry to children
    groupedByVoucher[voucherId].children.push({
      id: `${voucherId}-${accountName}`,
      accountName,
      debitAmt,
      creditAmt,
    });

    // Update totals
    groupedByVoucher[voucherId].debitAmt += debitAmt;
    groupedByVoucher[voucherId].creditAmt += creditAmt;
  });

  // Process each group to finalize data
  const result = Object.values(groupedByVoucher).map((group) => {
    // Format amounts to 2 decimal places
    group.debitAmt = parseFloat(group.debitAmt.toFixed(2));
    group.creditAmt = parseFloat(group.creditAmt.toFixed(2));

    // Set accountName to display total entries
    group.accountName = `${group.children.length} entries`;

    // Format date
    if (group.voucherDate) {
      const date = new Date(group.voucherDate);
      if (!isNaN(date.getTime())) {
        group.voucherDate = date.toLocaleDateString();
      }
    }

    // Process children
    group.children.forEach((child: any) => {
      child.debitAmt = parseFloat(child.debitAmt.toFixed(2));
      child.creditAmt = parseFloat(child.creditAmt.toFixed(2));
    });

    return group;
  });

  // Sort by voucherId in descending order (newest first)
  return result.sort((a, b) => b.voucherId - a.voucherId);
};

export const enabledReports = [
  {
    type: "journal",
    label: "journal",
  },
  {
    type: "chartofaccounts",
    label: "Chart of Accounts",
  },
];
